import PinEfectorSelect from "./PinEfectorSelect";
// src/components/Efectores/CrearEfector.jsx
import React, { useEffect, useState } from "react";
import GuiaConexion from "../Dispositivos/GuiaConexion";
import efectorService from "../../services/efectorService";
import { obtenerMisDispositivos } from "../../services/dispositivoService";
import usuarioService from "../../services/usuarioService";

/** Pines válidos para EFECTORES (ESP32 DevKit v1)
 *  - Excluimos: 34–39 (solo entrada), 6–11 (flash), 1/3 (UART0 consola)
 *  - Recomendados: 16,17,18,19,21,22,23,25,26,27,32,33
 *  - Avanzados:    0,2,4,5,12,13,14,15
 */

const toPinString = (n) => (Number.isFinite(+n) ? `D${Number(n)}` : "");
const validarPinD = (valor) => /^D\d+$/.test(valor || "");

const CrearEfector = ({ initialDispositivoId, onCreated }) => {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [pinNumber, setPinNumber] = useState("");
  const [dispositivos, setDispositivos] = useState([]);
  const [dispositivoId, setDispositivoId] = useState(String(initialDispositivoId || new URLSearchParams(window.location.search).get("dispositivoId") || ""));
  const [tiposEfector, setTiposEfector] = useState([]);
  const [tipoEfectorId, setTipoEfectorId] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosSeleccionados, setUsuariosSeleccionados] = useState([]);

  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const [nuevoEfectorId, setNuevoEfectorId] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);


  useEffect(() => {
    (async () => {
      try {
        const [dispositivosData, tiposEfectorData, usuariosData] = await Promise.all([
          obtenerMisDispositivos(),
          efectorService.obtenerTiposEfector(),
          usuarioService.obtenerUsuariosCreadosPorAdmin(),
        ]);

        // 🔒 Normalizaciones defensivas
        const dispositivosArr = Array.isArray(dispositivosData)
          ? dispositivosData
          : (dispositivosData?.data ?? dispositivosData?.items ?? []);
        const tiposEfectorArr = Array.isArray(tiposEfectorData)
          ? tiposEfectorData
          : (tiposEfectorData?.data ?? tiposEfectorData?.items ?? []);
        const usuariosArr = Array.isArray(usuariosData)
          ? usuariosData
          : (usuariosData?.data ?? usuariosData?.usuarios ?? usuariosData?.items ?? []);

        setDispositivos(dispositivosArr);
        setTiposEfector(tiposEfectorArr);
        setUsuarios(usuariosArr);
      } catch (err) {
        console.error("Error cargando datos:", err);
        setError("❌ Error al cargar los datos iniciales.");
      }
    })();
  }, []);

  const togglePermiso = (id) => {
    setUsuariosSeleccionados((prev) =>
      prev.some((uid) => String(uid) === String(id))
        ? prev.filter((uid) => String(uid) !== String(id))
        : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje("");
    setError("");
    setNuevoEfectorId(null);

    const pinActivacion = toPinString(pinNumber);

    if (!nombre.trim() || !pinNumber || !dispositivoId || !tipoEfectorId) {
      setError("Completa el nombre, tipo, dispositivo y pin del efector.");
      return;
    }
    if (!validarPinD(pinActivacion)) {
      setError("El pin debe tener el formato D seguido de un número (ej. D22).");
      return;
    }

    const payload = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim(),
      tipoEfectorId: Number(tipoEfectorId),
      dispositivoId: Number(dispositivoId),
      pinActivacion,
      permisos: usuariosSeleccionados.map(Number),
      encendido: false,
      estaActivo: true,
    };

    try {
      setGuardando(true);
      const resp = await efectorService.crearEfector(payload);
      onCreated?.(resp);

      const creadoId = resp?.efectorId ?? resp?.id ?? null;
      if (creadoId) {
        setNuevoEfectorId(creadoId);

        // Aplica estado inicial (encendido=false) al dispositivo
        try {
          setSincronizando(true);
          await efectorService.aplicarEstado(creadoId);
          setMensaje("✅ Efector creado y sincronizado con el dispositivo.");
          setError("");
        } catch (err) {
          console.error("Error al aplicar estado:", err);
          setError("⚠️ Efector creado, pero no se pudo aplicar el estado en el dispositivo.");
        } finally {
          setSincronizando(false);
        }
      } else {
        setMensaje("Efector creado correctamente.");
      }

      // limpiar formulario
      setNombre("");
      setDescripcion("");
      setPinNumber("");
      setDispositivoId(String(initialDispositivoId || new URLSearchParams(window.location.search).get("dispositivoId") || ""));
      setTipoEfectorId("");
      setUsuariosSeleccionados([]);
    } catch (err) {
      console.error("Error al crear el efector:", err);
      const status = err?.response?.status;
      if (status === 401) setError("No autorizado. Vuelve a iniciar sesión.");
      else if (status === 403) setError("No tienes permisos sobre ese dispositivo.");
      else {
        const detalle = err?.response?.data;
        setError(typeof detalle === "string" ? detalle : detalle?.mensaje || "Error al crear el efector.");
      }
    } finally {
      setGuardando(false);
    }
  };

  const sincronizarAhora = async () => {
    if (!nuevoEfectorId) return;
    try {
      setSincronizando(true);
      await efectorService.aplicarEstado(nuevoEfectorId);
      setMensaje("✅ Sincronización enviada con éxito.");
      setError("");
    } catch (err) {
      console.error("Error al sincronizar ahora:", err);
      setError("No se pudo sincronizar el estado con el dispositivo.");
    } finally {
      setSincronizando(false);
    }
  };

  return (
    <div className="bg-neutral-800 p-4 sm:p-6 rounded-lg shadow-md w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto">
      <h3 className="text-green-400 text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
        Crear Nuevo Efector
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label htmlFor="efector-nombre" className="block text-white mb-1 text-sm sm:text-base">Nombre del Efector</label>
            <input id="efector-nombre"
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            />
          </div>

          <div className="min-w-0">
            <label htmlFor="efector-descripcion" className="block text-white mb-1 text-sm sm:text-base">Descripción</label>
            <input id="efector-descripcion"
              type="text"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="efector-tipo" className="block text-white mb-1 text-sm sm:text-base">Tipo de Efector</label>
            <select id="efector-tipo"
              value={tipoEfectorId}
              onChange={(e) => setTipoEfectorId(e.target.value)}
              className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            >
              <option value="">Seleccione</option>
              {Array.isArray(tiposEfector) &&
                tiposEfector.map((tp) => (
                  <option key={tp.id} value={tp.id}>
                    {tp.nombre}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label htmlFor="efector-dispositivo" className="block text-white mb-1 text-sm sm:text-base">Dispositivo</label>
            <select id="efector-dispositivo"
              value={dispositivoId}
              onChange={(e) => { setDispositivoId(e.target.value); setPinNumber(""); }}
              className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            >
              <option value="">Seleccione</option>
              {Array.isArray(dispositivos) &&
                dispositivos.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nombre}
                  </option>
                ))}
            </select>
          </div>

          <div className="min-w-0">
            <label htmlFor="efector-pin" className="block text-white mb-1 text-sm sm:text-base">Pin de Activación</label>
            {!dispositivoId && <p className="text-sm text-amber-300 mb-2">Selecciona primero el dispositivo para consultar sus GPIO libres.</p>}
            <PinEfectorSelect id="efector-pin" dispositivoId={dispositivoId}
              value={pinNumber}
              onChange={(e) => setPinNumber(e.target.value)} className="w-full p-2 rounded bg-neutral-700 text-white" />
            <p className="text-xs text-neutral-400 mt-1">
              Evita GPIO 34–39 (solo entrada), 6–11 (flash) y 1/3 (UART0). Los pines reservados no se ofrecen en este perfil.
            </p>
            <div className="mt-3"><GuiaConexion pin={pinNumber ? `D${pinNumber}` : ""} efector /></div>
          </div>

          <div className="min-w-0">
            <label className="block text-white mb-2 text-sm sm:text-base">
              Permisos (usuarios que podrán ver este efector)
            </label>
            <div className="max-h-40 sm:max-h-60 overflow-y-auto border border-neutral-600 p-2 rounded bg-neutral-900">
              {Array.isArray(usuarios) && usuarios.length > 0 ? (
                usuarios.map((u) => (
                  <label key={u.id} className="flex items-center text-white text-sm sm:text-base py-0.5">
                    <input
                      type="checkbox"
                      checked={usuariosSeleccionados.some((id) => String(id) === String(u.id))}
                      onChange={() => togglePermiso(u.id)}
                      className="mr-2"
                    />
                    {u.nombre} ({u.email})
                  </label>
                ))
              ) : (
                <p className="text-neutral-400 text-sm">No hay usuarios temporales disponibles.</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={guardando}
            className={`w-full sm:w-auto text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded ${
              guardando ? "bg-gray-600" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {guardando ? "Guardando..." : "Guardar Efector"}
          </button>
        </div>
      </form>

      {nuevoEfectorId && (
        <div className="mt-4 p-3 sm:p-4 rounded border border-cyan-500 bg-neutral-900">
          <p className="text-cyan-300 text-sm sm:text-base">
            El efector fue creado con <strong>encendido = false</strong> y se intentó aplicar al dispositivo.
          </p>
          <div className="mt-2">
            <button
              onClick={sincronizarAhora}
              disabled={sincronizando}
              className={`w-full sm:w-auto px-4 sm:px-5 py-2 rounded text-white ${
                sincronizando ? "bg-gray-600" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {sincronizando ? "Sincronizando..." : "Sincronizar ahora"}
            </button>
          </div>
        </div>
      )}

      {mensaje && <p className="text-green-400 mt-4 text-sm sm:text-base">{mensaje}</p>}
      {error && <p className="text-red-400 mt-4 text-sm sm:text-base">{error}</p>}
    </div>
  );
};

export default CrearEfector;
